/* ==========================================================================
   NeuroSync Protocol — Product Page JavaScript
   Scroll animations, FAQ accordion, testimonial carousel, sticky cart bar,
   subscription toggle, video controls.

   Uses event delegation so handlers survive Shopify Theme Editor re-renders.
   ========================================================================== */

(function () {
  'use strict';

  var isDesignMode = !!(window.Shopify && Shopify.designMode);

  /* ---------- 1. Scroll-triggered reveal animations ---------- */
  var revealObserver = null;

  function initRevealAnimations(scope) {
    scope = scope || document;

    /* In the Theme Editor, show everything immediately — no animation */
    if (isDesignMode) {
      scope.querySelectorAll('.ns-reveal').forEach(function (el) {
        el.classList.add('is-visible');
      });
      return;
    }

    scope.querySelectorAll('.ns-reveal-stagger').forEach(function (parent) {
      var children = parent.querySelectorAll('.ns-reveal');
      children.forEach(function (child, i) {
        child.style.setProperty('--ns-stagger', i);
      });
    });

    if (!revealObserver) {
      revealObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            revealObserver.unobserve(entry.target);
          }
        });
      }, { threshold: 0.15, rootMargin: '0px 0px -40px 0px' });
    }

    scope.querySelectorAll('.ns-reveal:not(.is-visible)').forEach(function (el) {
      revealObserver.observe(el);
    });
  }

  /* ---------- 2. FAQ / Ingredient Accordion (event delegation) ---------- */
  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('.ns-accordion__trigger');
    if (!trigger) return;

    var item = trigger.closest('.ns-accordion__item');
    if (!item) return;
    var panel = item.querySelector('.ns-accordion__panel');
    if (!panel) return;
    var isOpen = item.classList.contains('is-open');

    var accordion = item.closest('.ns-accordion');
    if (accordion) {
      accordion.querySelectorAll('.ns-accordion__item.is-open').forEach(function (openItem) {
        if (openItem !== item) {
          openItem.classList.remove('is-open');
          var openPanel = openItem.querySelector('.ns-accordion__panel');
          if (openPanel) openPanel.style.maxHeight = '0';
          var openTrigger = openItem.querySelector('.ns-accordion__trigger');
          if (openTrigger) openTrigger.setAttribute('aria-expanded', 'false');
        }
      });
    }

    if (isOpen) {
      item.classList.remove('is-open');
      panel.style.maxHeight = '0';
      trigger.setAttribute('aria-expanded', 'false');
    } else {
      item.classList.add('is-open');
      panel.style.maxHeight = panel.scrollHeight + 'px';
      trigger.setAttribute('aria-expanded', 'true');
    }
  });

  /* ---------- 3. Testimonials Carousel (event delegation) ---------- */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('.ns-testimonials__nav-btn');
    if (!btn) return;

    var carousel = document.querySelector('.ns-testimonials__carousel');
    if (!carousel) return;

    var card = carousel.querySelector('.ns-testimonial-card');
    var scrollAmount = card ? card.offsetWidth + 24 : 340;

    if (btn.classList.contains('ns-testimonials__nav-btn--prev')) {
      carousel.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
    } else {
      carousel.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  });

  /* ---------- 4. Sticky Add-to-Cart Bar ---------- */
  var stickyObserver = null;

  function initStickyBar() {
    var stickyBar = document.querySelector('.ns-sticky-bar');
    var heroCtaAnchor = document.querySelector('.ns-hero__cta-row');
    if (!stickyBar || !heroCtaAnchor) return;

    if (stickyObserver) stickyObserver.disconnect();

    stickyObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          stickyBar.classList.remove('is-visible');
        } else {
          stickyBar.classList.add('is-visible');
        }
      });
    }, { threshold: 0 });

    stickyObserver.observe(heroCtaAnchor);
  }

  /* ---------- 5. Subscribe & Save Toggle (event delegation) ---------- */
  document.addEventListener('click', function (e) {
    var option = e.target.closest('.ns-subscribe-toggle__option');
    if (!option) return;

    var toggle = option.closest('.ns-subscribe-toggle');
    if (!toggle) return;

    toggle.querySelectorAll('.ns-subscribe-toggle__option').forEach(function (o) {
      o.classList.remove('is-active');
    });
    option.classList.add('is-active');

    var mode = option.getAttribute('data-mode');

    /* Sync ALL subscribe toggles on the page */
    document.querySelectorAll('.ns-subscribe-toggle').forEach(function (otherToggle) {
      if (otherToggle === toggle) return;
      otherToggle.querySelectorAll('.ns-subscribe-toggle__option').forEach(function (o) {
        o.classList.remove('is-active');
        if (o.getAttribute('data-mode') === mode) o.classList.add('is-active');
      });
    });

    /* Update selling_plan inputs on ALL forms */
    document.querySelectorAll('.ns-selling-plan-input').forEach(function (input) {
      if (mode === 'subscribe') {
        input.value = input.getAttribute('data-selling-plan-id');
      } else {
        input.value = '';
      }
    });

    /* Update ALL pricing containers on the page */
    document.querySelectorAll('[data-ns-pricing]').forEach(function (container) {
      var subDetails = container.querySelector('[data-ns-sub-details]');
      var subBonuses = container.querySelector('[data-ns-sub-bonuses]');
      var onetimeDetails = container.querySelector('[data-ns-onetime-details]');
      var saleTiers = container.querySelector('[data-ns-tiers]');
      var packsActive = container.hasAttribute('data-ns-packs-active');
      var priceOld = container.querySelector('.ns-price-old, .ns-hero__price-old');
      var priceCurrent = container.querySelector('.ns-price-current, .ns-hero__price-new');

      if (mode === 'subscribe') {
        if (subDetails) subDetails.style.display = '';
        if (subBonuses) subBonuses.style.display = '';
        if (onetimeDetails) onetimeDetails.style.display = 'none';
        if (saleTiers) saleTiers.style.display = 'none';
        if (priceOld) priceOld.style.display = '';
        if (priceCurrent) priceCurrent.textContent = priceCurrent.getAttribute('data-subscribe-price') || priceCurrent.textContent;
        /* Reset qty to 1 when switching to subscribe (subscriptions are per-period) */
        var qtyInput = container.querySelector('.ns-hero__qty-input');
        if (qtyInput) qtyInput.value = 1;
      } else {
        if (subDetails) subDetails.style.display = 'none';
        if (subBonuses) subBonuses.style.display = 'none';
        if (packsActive && saleTiers) {
          saleTiers.style.display = '';
          if (onetimeDetails) onetimeDetails.style.display = 'none';
          /* Apply currently selected tier qty */
          var selTier = saleTiers.querySelector('.ns-tier.is-selected');
          var qtyInput2 = container.querySelector('.ns-hero__qty-input');
          if (selTier && qtyInput2) qtyInput2.value = selTier.getAttribute('data-tier-qty') || 1;
        } else {
          if (onetimeDetails) onetimeDetails.style.display = '';
        }
        if (priceOld) priceOld.style.display = 'none';
        if (priceCurrent) priceCurrent.textContent = priceCurrent.getAttribute('data-onetime-price') || '$139.99';
      }
    });

    /* Keep the sticky bar's quantity + price aligned with the new mode:
       subscribe = qty 1 at the subscription price; one-time = the selected pack. */
    if (mode === 'subscribe') {
      var subPriceEl = document.querySelector('.ns-price-current');
      syncPackSelection(1, subPriceEl && subPriceEl.getAttribute('data-subscribe-price'));
    } else {
      var selTierNow = document.querySelector('[data-ns-packs-active] .ns-tier.is-selected');
      if (selTierNow) {
        syncPackSelection(parseInt(selTierNow.getAttribute('data-tier-qty'), 10) || 1, selTierNow.getAttribute('data-tier-total'));
      }
    }
  });

  /* Mirror the active pack selection to EVERY product form on the page
     (hero + sticky bar) so any Add-to-Cart submits the right quantity, and
     keep the sticky bar price in sync with the chosen pack. Without this the
     sticky bar would always add quantity 1 regardless of the selected pack. */
  function syncPackSelection(qty, totalPrice) {
    if (qty != null) {
      document.querySelectorAll('.ns-qty-sync').forEach(function (input) {
        input.value = qty;
      });
    }
    if (totalPrice) {
      document.querySelectorAll('[data-ns-sticky-price]').forEach(function (el) {
        el.textContent = totalPrice;
      });
    }
  }

  /* ---------- 5b. Sale tier selection (event delegation) ---------- */
  document.addEventListener('click', function (e) {
    var tier = e.target.closest('.ns-tier');
    if (!tier) return;
    var tiers = tier.closest('[data-ns-tiers]');
    if (!tiers) return;
    tiers.querySelectorAll('.ns-tier').forEach(function (t) {
      t.classList.remove('is-selected');
      t.setAttribute('aria-checked', 'false');
    });
    tier.classList.add('is-selected');
    tier.setAttribute('aria-checked', 'true');
    var qty = parseInt(tier.getAttribute('data-tier-qty'), 10) || 1;
    var container = tier.closest('[data-ns-pricing]');
    if (container) {
      var input = container.querySelector('.ns-hero__qty-input');
      if (input) input.value = qty;
    }
    syncPackSelection(qty, tier.getAttribute('data-tier-total'));
  });

  /* ---------- 5b-2. Authoritative quantity at add-to-cart time ----------
     The theme's own quantity handling can reset the hero's visible quantity
     input back to 1 after our sync runs. To guarantee the cart always gets the
     quantity of the SELECTED pack (or 1 for subscribe), we re-write the form's
     quantity input in the CAPTURE phase of the Add-to-Cart click/submit, i.e.
     before any theme handler reads it. This makes the pack -> quantity wiring
     immune to load-order/reset races. */
  function correctQtyForForm(form) {
    if (!form) return;
    var qtyInput = form.querySelector('input[name="quantity"]');
    if (!qtyInput) return;
    var subActive = document.querySelector('.ns-subscribe-toggle__option[data-mode="subscribe"].is-active');
    if (subActive) { qtyInput.value = 1; return; }
    var container = form.closest('[data-ns-pricing]');
    var selTier = (container && container.querySelector('.ns-tier.is-selected')) || document.querySelector('.ns-tier.is-selected');
    if (selTier) {
      var q = parseInt(selTier.getAttribute('data-tier-qty'), 10);
      if (q > 0) qtyInput.value = q;
    }
  }
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('.ns-hero__cta-btn, .ns-sticky-bar .ns-btn, .ns-buy-box__cta');
    if (btn) correctQtyForForm(btn.closest('form'));
  }, true);
  document.addEventListener('submit', function (e) {
    if (e.target && e.target.matches && e.target.matches('form[action*="/cart/add"]')) correctQtyForForm(e.target);
  }, true);

  /* ---------- 5c. Sale countdown timer ---------- */
  function startCountdown(banner) {
    var endStr = banner.getAttribute('data-end');
    if (!endStr) return;
    var endTs = new Date(endStr).getTime();
    if (isNaN(endTs)) return;
    /* Evergreen loop: when set, the deadline rolls forward every N days so the
       timer auto-restarts instead of hiding at zero. */
    var loopDays = parseFloat(banner.getAttribute('data-loop-days')) || 0;
    var loopMs = loopDays > 0 ? loopDays * 86400000 : 0;
    if (loopMs > 0 && Date.now() >= endTs) {
      endTs += (Math.floor((Date.now() - endTs) / loopMs) + 1) * loopMs;
    }
    var d = banner.querySelector('[data-cd-d]');
    var h = banner.querySelector('[data-cd-h]');
    var m = banner.querySelector('[data-cd-m]');
    var s = banner.querySelector('[data-cd-s]');
    if (!d || !h || !m || !s) return;

    function tick() {
      var diff = endTs - Date.now();
      if (diff <= 0 && loopMs > 0) {
        endTs += (Math.floor((Date.now() - endTs) / loopMs) + 1) * loopMs;
        diff = endTs - Date.now();
      }
      diff = Math.max(0, diff);
      var dd = Math.floor(diff / 86400000); var rem = diff % 86400000;
      var hh = Math.floor(rem / 3600000); rem = rem % 3600000;
      var mm = Math.floor(rem / 60000); rem = rem % 60000;
      var ss = Math.floor(rem / 1000);
      d.textContent = dd;
      h.textContent = String(hh).padStart(2, '0');
      m.textContent = String(mm).padStart(2, '0');
      s.textContent = String(ss).padStart(2, '0');
      if (diff <= 0 && loopMs <= 0) {
        clearInterval(banner._cdInterval);
        banner.style.display = 'none';
      }
    }
    tick();
    if (banner._cdInterval) clearInterval(banner._cdInterval);
    banner._cdInterval = setInterval(tick, 1000);
  }

  function initCountdowns(scope) {
    scope = scope || document;
    scope.querySelectorAll('[data-ns-md-banner]').forEach(startCountdown);
  }

  /* ---------- 6. Video sound toggle (event delegation) ---------- */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('.ns-timeline__sound-toggle');
    if (!btn) return;
    var wrapper = btn.closest('.ns-timeline__video');
    if (!wrapper) return;
    var video = wrapper.querySelector('video');
    if (!video) return;
    video.muted = !video.muted;
    wrapper.classList.toggle('is-unmuted', !video.muted);
  });

  /* ---------- 7a. Video testimonials — transform-based carousel (desktop) ---------- */
  var vtIndex = 0;

  function vtIsDesktop() { return window.innerWidth >= 900; }

  function vtGetTrackInfo() {
    var track = document.querySelector('.ns-video-testimonials__track');
    if (!track) return null;
    var cards = track.querySelectorAll('.ns-video-testimonials__card');
    if (!cards.length) return null;
    var gap = 20;
    var cardW = cards[0].offsetWidth;
    var step = cardW + gap;
    var wrapperW = track.parentElement.offsetWidth;
    var totalW = cards.length * cardW + (cards.length - 1) * gap;
    var maxOffset = Math.max(0, totalW - wrapperW);
    var maxIndex = Math.max(0, Math.ceil(maxOffset / step));
    return { track: track, cards: cards, step: step, maxIndex: maxIndex, maxOffset: maxOffset };
  }

  function vtUpdate() {
    var info = vtGetTrackInfo();
    if (!info) return;
    if (!vtIsDesktop()) {
      info.track.style.transform = '';
      return;
    }
    var offset = Math.min(vtIndex * info.step, info.maxOffset);
    info.track.style.transform = 'translateX(-' + offset + 'px)';

    /* Update fade hint */
    var wrapper = info.track.closest('.ns-video-testimonials__track-wrapper');
    if (wrapper) wrapper.classList.toggle('is-at-end', vtIndex >= info.maxIndex);

    /* Update button states */
    var section = info.track.closest('.ns-video-testimonials');
    if (section) {
      var prev = section.querySelector('.ns-video-testimonials__nav-btn--prev');
      var next = section.querySelector('.ns-video-testimonials__nav-btn--next');
      if (prev) prev.disabled = vtIndex <= 0;
      if (next) next.disabled = vtIndex >= info.maxIndex;
    }
  }

  /* Arrow nav */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('.ns-video-testimonials__nav-btn');
    if (!btn) return;
    var info = vtGetTrackInfo();
    if (!info) return;

    if (btn.classList.contains('ns-video-testimonials__nav-btn--prev')) {
      vtIndex = Math.max(0, vtIndex - 1);
    } else {
      vtIndex = Math.min(info.maxIndex, vtIndex + 1);
    }
    vtUpdate();
  });

  /* Reset transform on resize / orientation change */
  window.addEventListener('resize', function () {
    if (!vtIsDesktop()) { vtIndex = 0; }
    vtUpdate();
  });

  /* Initial button state after load */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', vtUpdate);
  } else {
    vtUpdate();
  }

  /* ---------- 7b. Video testimonial play/pause (event delegation) ---------- */
  var currentlyPlaying = null;
  document.addEventListener('click', function (e) {
    var player = e.target.closest('.ns-video-testimonials__player');
    if (!player) return;

    var video = player.querySelector('video');
    if (!video) return;
    var card = player.closest('.ns-video-testimonials__card');
    if (!card) return;

    /* On desktop, slide the clicked card into view */
    if (vtIsDesktop()) {
      var info = vtGetTrackInfo();
      if (info) {
        var cards = Array.prototype.slice.call(info.cards);
        var idx = cards.indexOf(card);
        if (idx >= 0 && idx > vtIndex + Math.floor(info.track.parentElement.offsetWidth / info.step) - 1) {
          vtIndex = Math.min(info.maxIndex, idx);
          vtUpdate();
        }
      }
    }

    if (currentlyPlaying && currentlyPlaying !== video) {
      currentlyPlaying.pause();
      currentlyPlaying.currentTime = 0;
      var prevCard = currentlyPlaying.closest('.ns-video-testimonials__card');
      if (prevCard) prevCard.classList.remove('is-playing');
    }

    if (video.paused) {
      video.muted = false;
      video.play().then(function () {
        card.classList.add('is-playing');
      }).catch(function () {
        /* Autoplay with sound blocked — retry muted */
        video.muted = true;
        video.play();
        card.classList.add('is-playing');
      });
      currentlyPlaying = video;
    } else {
      video.pause();
      card.classList.remove('is-playing');
      currentlyPlaying = null;
    }
  });

  /* ---------- 8. Hero thumbnail gallery (event delegation) ---------- */
  document.addEventListener('click', function (e) {
    var thumb = e.target.closest('.ns-hero__thumb');
    if (!thumb) return;
    var mainImg = document.getElementById('ns-hero-main-img');
    if (!mainImg) return;

    document.querySelectorAll('.ns-hero__thumb').forEach(function (t) {
      t.classList.remove('is-active');
    });
    thumb.classList.add('is-active');
    /* Clear srcset/sizes — they take precedence over src and would override our change */
    mainImg.removeAttribute('srcset');
    mainImg.removeAttribute('sizes');
    mainImg.src = thumb.getAttribute('data-full-src');
    mainImg.alt = thumb.getAttribute('data-alt');
  });

  /* ---------- 9. Buy-box variant select (event delegation) ---------- */
  document.addEventListener('change', function (e) {
    if (!e.target.matches('.ns-variant-select')) return;
    var hidden = document.querySelector('.ns-buy-box .ns-buybox-variant-id');
    if (hidden) hidden.value = e.target.value;
  });

  /* ---------- 10. Quantity stepper (event delegation) ---------- */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-qty-action]');
    if (!btn) return;
    var wrap = btn.closest('[data-ns-qty]');
    if (!wrap) return;
    var input = wrap.querySelector('.ns-hero__qty-input');
    if (!input) return;
    var val = parseInt(input.value, 10);
    if (isNaN(val) || val < 1) val = 1;
    var max = parseInt(input.getAttribute('max'), 10) || 99;
    var action = btn.getAttribute('data-qty-action');
    if (action === 'increase') val = Math.min(max, val + 1);
    else if (action === 'decrease') val = Math.max(1, val - 1);
    input.value = val;
  });

  document.addEventListener('input', function (e) {
    if (!e.target.matches('.ns-hero__qty-input')) return;
    var v = parseInt(e.target.value, 10);
    if (isNaN(v) || v < 1) e.target.value = 1;
  });

  document.addEventListener('blur', function (e) {
    if (!e.target.matches || !e.target.matches('.ns-hero__qty-input')) return;
    var v = parseInt(e.target.value, 10);
    if (isNaN(v) || v < 1) e.target.value = 1;
  }, true);

  /* ---------- 11. Pre-warm lazy images before they enter viewport ----------
     Native loading="lazy" uses a fixed browser threshold (~125% of viewport).
     On fast desktop scroll, images can still pop in late. This observer
     warms the browser cache 1500px before they're visible by creating a
     hidden Image() preloader. When the real <img> is then needed, it
     decodes instantly from cache. */
  var lazyPrewarmObserver = null;

  function initLazyPrewarm(scope) {
    scope = scope || document;
    if (!('IntersectionObserver' in window)) return;
    if (!lazyPrewarmObserver) {
      lazyPrewarmObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var img = entry.target;
          var preloader = new Image();
          if (img.srcset) preloader.srcset = img.srcset;
          if (img.sizes) preloader.sizes = img.sizes;
          if (img.currentSrc || img.src) preloader.src = img.currentSrc || img.src;
          lazyPrewarmObserver.unobserve(img);
        });
      }, { rootMargin: '1500px 0px', threshold: 0 });
    }
    scope.querySelectorAll('img[loading="lazy"]').forEach(function (img) {
      if (img.complete) return;
      lazyPrewarmObserver.observe(img);
    });
  }

  /* ---------- 12. Default to One-time when sale is active ----------
     ns-hero is server-rendered in the right state via section.settings.sale_enabled.
     This init syncs the buy-box toggle, clears all selling_plan inputs, and
     flips price displays so all sections agree on first paint. */
  function initSaleMode() {
    if (!document.querySelector('[data-ns-sale-active]')) return;

    document.querySelectorAll('.ns-subscribe-toggle').forEach(function (t) {
      t.querySelectorAll('.ns-subscribe-toggle__option').forEach(function (o) {
        o.classList.toggle('is-active', o.getAttribute('data-mode') === 'onetime');
      });
    });

    document.querySelectorAll('.ns-selling-plan-input').forEach(function (input) {
      input.value = '';
    });

    document.querySelectorAll('.ns-price-current').forEach(function (p) {
      var oneTime = p.getAttribute('data-onetime-price');
      if (oneTime) p.textContent = oneTime;
    });

    document.querySelectorAll('.ns-price-old, .ns-hero__price-old').forEach(function (p) {
      p.style.display = 'none';
    });

    /* Sync qty input in ns-hero to the default-selected tier's qty */
    document.querySelectorAll('[data-ns-sale-active]').forEach(function (container) {
      var selectedTier = container.querySelector('.ns-tier.is-selected');
      var qtyInput = container.querySelector('.ns-hero__qty-input');
      if (selectedTier && qtyInput) {
        qtyInput.value = selectedTier.getAttribute('data-tier-qty') || 1;
      }
    });

    /* Propagate the default pack selection to the sticky bar (qty + price) on load */
    var defaultTier = document.querySelector('[data-ns-sale-active] .ns-tier.is-selected');
    if (defaultTier) {
      syncPackSelection(parseInt(defaultTier.getAttribute('data-tier-qty'), 10) || 1, defaultTier.getAttribute('data-tier-total'));
    }
  }

  /* ---------- Init ---------- */
  function init() {
    initRevealAnimations();
    initStickyBar();
    initCountdowns();
    initLazyPrewarm();
    initSaleMode();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* ---------- Shopify Theme Editor Integration ---------- */
  document.addEventListener('shopify:section:load', function (event) {
    initRevealAnimations(event.target);
    initStickyBar();
    initCountdowns(event.target);
    initLazyPrewarm(event.target);
    initSaleMode();
  });

  document.addEventListener('shopify:section:select', function (event) {
    /* Ensure section content is visible when selected in the editor */
    event.target.querySelectorAll('.ns-reveal').forEach(function (el) {
      el.classList.add('is-visible');
    });
  });
})();
